import { computed, reactive, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { CLOSE_CONFIRM_DISCARD, CLOSE_CONFIRM_PRESERVE } from '@/constants/closeConfirmCopy'

export type CloseConfirmVariant = 'discard' | 'preserve'

export interface UseDirtyBeforeDismissOptions {
  isDirty: MaybeRefOrGetter<boolean>
  variant: CloseConfirmVariant
  title?: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'purple' | 'ghost'
  dialogLayout?: 'default' | 'centered'
}

/**
 * Coordinates BaseModal/BaseSidePanel beforeDismiss with a single BaseConfirmDialog.
 * Call beforeDismiss(complete) from the overlay; on user confirm, complete() runs to finish close.
 */
export function useDirtyBeforeDismiss(options: UseDirtyBeforeDismissOptions) {
  const showCloseConfirm = ref(false)
  let pendingComplete: (() => void) | null = null

  const baseCopy = computed(() =>
    options.variant === 'discard' ? CLOSE_CONFIRM_DISCARD : CLOSE_CONFIRM_PRESERVE
  )

  const confirmDialogTitle = computed(() => options.title ?? baseCopy.value.title)
  const confirmDialogMessage = computed(() => options.message ?? baseCopy.value.message)
  const confirmDialogConfirmLabel = computed(() => options.confirmLabel ?? baseCopy.value.confirmLabel)
  const confirmDialogCancelLabel = computed(() => options.cancelLabel ?? baseCopy.value.cancelLabel)
  const confirmDialogVariant = computed(
    () => options.confirmVariant ?? baseCopy.value.confirmVariant
  )
  const confirmDialogLayout = computed(
    () => options.dialogLayout ?? baseCopy.value.dialogLayout
  )

  function beforeDismiss(complete: () => void) {
    const dirty = toValue(options.isDirty)
    if (!dirty) {
      complete()
      return
    }
    pendingComplete = complete
    showCloseConfirm.value = true
  }

  function onCloseConfirmConfirm() {
    showCloseConfirm.value = false
    const done = pendingComplete
    pendingComplete = null
    done?.()
  }

  function onCloseConfirmCancel() {
    showCloseConfirm.value = false
    pendingComplete = null
  }

  // Wrap in reactive so nested refs/computeds unwrap in templates (e.g. dirtyClose.showCloseConfirm).
  // A plain object would pass Ref objects through to child props and break prop type checks.
  return reactive({
    showCloseConfirm,
    beforeDismiss,
    onCloseConfirmConfirm,
    onCloseConfirmCancel,
    confirmDialogTitle,
    confirmDialogMessage,
    confirmDialogConfirmLabel,
    confirmDialogCancelLabel,
    confirmDialogVariant,
    confirmDialogLayout
  })
}
